import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LangsProvider } from '../../../common/providers/langs-provider';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from '../../../common/providers/current-user';
import { TeamRow, TeamsProvider } from '../../providers/teams-provider';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose, MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { User } from "../../../common/models/user";
import { Organization } from "../../../common/models/organization";
import { Lang } from "../../../common/models/lang";
import { AsyncPipe, NgForOf, NgIf } from "@angular/common";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatOption } from "@angular/material/autocomplete";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatFormField } from "@angular/material/form-field";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { take } from "rxjs/operators";

@Component({
  selector: 'teams-coaches-form',
  providers: [],
  styleUrls: ['./coaches.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogClose,
    NgForOf,
    MatDialogTitle,
    NgIf,
    MatButton,
    MatIconButton,
    MatCheckbox,
    MatFormField,
    MatProgressBar,
    MatTooltip,
    TranslateModule,
    MatDialogActions,
    MatDialogContent,
    MatIcon,
    MatSelectModule,
    NgxMatSelectSearchModule,
    AsyncPipe
  ],
  templateUrl: './coaches.html'
})
export class TeamsCoachesFormComponent implements OnInit, AfterViewInit, OnDestroy {
  public isLoading = false;
  public formTitle = '';
  public confirmButtonTitle = 'Save';
  public organisation: Organization = {} as Organization;
  public teamId = 0;
  public teamRow: TeamRow = {} as TeamRow;
  public langId: number = Lang.ID_DEFAULT;

  @ViewChild('multiSelect', { static: true }) multiSelect!: MatSelect;
  public availableCoaches: User[] = [];
  public coachesSelected: User[] = [];
  public coachSelectFormControl: FormControl<User[]> = new FormControl<User[]>([]) as FormControl<User[]>;
  public coachFilterFormControl: FormControl<string> = new FormControl<string>('') as FormControl<string>;
  public filteredCoaches: ReplaySubject<User[]> = new ReplaySubject<User[]>(1);
  protected _onDestroy = new Subject<void>();

  public removeCoachAthleteRelations = true;
  constructor(
    public conf: ConfigProvider,
    public currentUser: CurrentUser,
    public utils: UtilsProvider,
    public dialog: MatDialogRef<TeamsCoachesFormComponent>,
    public usersProvider: UsersProvider,
    private _toaster: MatSnackBar,
    private _title: Title,
    private _router: Router,
    private _route: ActivatedRoute,
    // private _dateAdapter: DateAdapter<any>,
    private _langsProvider: LangsProvider,
    private _translater: TranslateService,
    private _teamProvider: TeamsProvider,
    @Inject(MAT_DIALOG_DATA) private _data : any
  ) {

    this.availableCoaches = this._data.availableCoaches;
    this.organisation = _data.organization;

    if (this._data.teamRow) {
      this.teamRow = this._data.teamRow;
      this.teamId = this._data.teamRow.team.id;
      this.coachesSelected = this.teamRow.coaches;
    }

    this.formTitle = this._data.formTitle;
    this.confirmButtonTitle = this._data.confirmButtonTitle;
  }

  public ngOnInit() {
    // Charger les coachs filtrés initialement
    this.filteredCoaches.next(this.availableCoaches.slice());

    // Écouter les changements dans le champ de recherche
    this.coachFilterFormControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterCoaches();
      });

    // Initialiser les coachs sélectionnés si nécessaire
    if (this.coachesSelected.length > 0) {
      this.coachSelectFormControl.setValue(
        this.coachesSelected
      );
    }
  }

  public ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  public ngAfterViewInit() {
    this.setInitialValue();
  }


  public onSaveClicked(): void {
    if (this.isValid()) {
      this.isLoading = true;
      this.save().subscribe({
        next: (result: TeamRow) => {
          this._toaster.open(this._translater.instant('Team_saved'), '', {duration: 5000});
          this.isLoading = false;
          this.dialog.close(result);
        },
        error: (error: any) => this.onError(error)
      });
    } else {
      this.touchStepFields();
      this._toaster.open(this._translater.instant('Correct_form_errors'), '', {duration: 5000});
    }
  }

  private isValid(): boolean {
    return !this.coachSelectFormControl.invalid;
  }

  private touchStepFields(): void {
    this.coachSelectFormControl.markAsTouched();
  }

  private save(): Observable<TeamRow> {
    const teamRow: TeamRow = {
      team: this.teamRow.team,
      coaches: [],
      athletes: [],
      coachNames: ''
    };

    // user (coach)
    if (this.coachSelectFormControl.value.length) {
      for (let i = 0; i < this.coachSelectFormControl.value.length; i++) {
        const coachUser = this.utils.findIn(
          this.coachSelectFormControl.value[i].id,
          this.availableCoaches,
          'id');
        if (coachUser) {
          teamRow.coaches.push(coachUser);
        }
      }
    }

    return this._teamProvider.saveTeamCoaches(this.organisation.id!, this.removeCoachAthleteRelations, teamRow)
  }


  protected setInitialValue() {
    this.filteredCoaches
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.multiSelect.compareWith = (a: User, b: User) => a && b && a.id === b.id;
      });
  }

  protected filterCoaches() {
    if (!this.availableCoaches) {
      return;
    }
    // get the search keyword
    let search = this.coachFilterFormControl.value;
    if (!search) {
      this.filteredCoaches.next(this.availableCoaches.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the banks
    this.filteredCoaches.next(
      this.availableCoaches.filter(coach => `${coach.firstName} ${coach.lastName}`.toLowerCase().indexOf(search) > -1)
    );
  }

  private onError(error: any): void {
    this.isLoading = false;

    if (error.status === 422) {
      if (error.error.errors && error.error.errors.email) {
        // c'est qu'on a essayé de faire valider un email test@test par exemple
        // (c'est validé par le validator angular mais pas laravel...)
        return;
      }
    }

    this._toaster.open(this._translater.instant('Error_unknown'), '', {duration: 5000});
  }


}
